import { hashString } from "@/api/mock-data/agent-estate/generate";
import {
  AUTHORIZATION_SERVERS,
  authorizationServerFor,
} from "@/api/mock-data/agent-estate/mcpConnectivity";
import { createScenarioSwitch } from "@/api/mock-data/agent-estate/scenarioSwitch";
import {
  type ClientProfileConnection,
  type GatewayProfileReference,
  agentEstateStore,
} from "@/api/mock-data/agent-estate/store";
import type { AccessPurpose } from "@/api/temp/McpAuthenticationService";
import {
  type AuthorizationProvider,
  type AuthorizationProviderList,
  type CreateProfileRequest,
  type CredentialAvailability,
  type EligibilityReason,
  type OAuthClientProfile,
  type ProfileAction,
  type ProfileActivity,
  type ProfileActivityType,
  type ProfileCheck,
  type ProfileCheckState,
  type ProfileEligibility,
  type ProfileError,
  type ProfileErrorCode,
  type ProfileFormOptions,
  type ProfileImpact,
  type ProfileInput,
  type ProfileLifecycle,
  type ProfileListQuery,
  type ProfileMetadataPreview,
  type ProfileOption,
  type ProfileOptionsResult,
  type ProfilePage,
  type ProfilePurpose,
  type ProfileRegistration,
  type ProfileSelectionContext,
  type ProfileUsage,
  type ProfileUsageReference,
  type ProfileUsageSummary,
  type ProfileValidation,
  type ProfileValidationJob,
  PROFILE_ERRORS,
  type UpdateProfileRequest,
  type UsageCompleteness,
  type ValidateProfileRequest,
} from "@/api/temp/OAuthClientProfileService";
import {
  currentTenant,
  isRecord,
  registerAgentSessionReset,
} from "@/libs/agentSession";
import {
  CIMD_SPECIFICATION_PROFILE,
  consequentialChanges,
  identityChanges,
  identityKey,
  isConcreteIssuer,
  isDraftSavable,
  isProfileComplete,
  needsAttention,
  profileInputFingerprint,
} from "@/pages/Administration/OAuthClientProfiles/profileModel";
import {
  DEMO_AUTHORIZATION_PROVIDERS,
  MANAGED_CREDENTIALS,
  PROFILE_SEEDS,
  type ProfileSeed,
  TENANT_ENVIRONMENTS,
} from "./seed";
import {
  credentialAvailabilityOf,
  documentFingerprintOf,
  runProfileChecks,
} from "./validation";

/**
 * Mock OAuth client-profile registry - one tenant-scoped store behind the
 * admin page AND the Authentication Setup picker, so a profile saved in one
 * appears in the other.
 *
 * Persisted to localStorage (synthetic, secret-free fixtures only), keyed by
 * tenant and scenario, and re-read on every call: a profile added in another
 * tab shows up here on the next refetch. Usage is never stored - it is
 * derived from the saved MCP connection configurations in `agentEstateStore`.
 *
 * Checks run as jobs that settle on the clock, so the UI sees "Checking"
 * before a result lands. A result is bound to the fingerprint of the inputs it
 * ran against and only counts for exactly those inputs.
 *
 * Scenario switch, inside mock scope: `?clientProfiles=`
 *  - `seeded` (default) - the fixtures in `seed.ts`;
 *  - `empty` - a tenant with no profiles yet;
 *  - `unavailable` - this environment has no client-profile registry;
 *  - `failing` - list and option requests fail;
 *  - `partialUsage` - usage visibility is partial;
 *  - `forbidden` - the caller may not view client profiles;
 *  - `providersFail` - the authorization-provider list request fails;
 *  - `providersEmpty` - no authorization provider applies to this tenant.
 */

export type ClientProfileScenario =
  | "seeded"
  | "empty"
  | "unavailable"
  | "failing"
  | "partialUsage"
  | "forbidden"
  | "providersFail"
  | "providersEmpty";

const scenarioSwitch = createScenarioSwitch<ClientProfileScenario>({
  param: "clientProfiles",
  storageKey: "__TLX_CLIENT_PROFILE_SCENARIO__",
  values: [
    "seeded",
    "empty",
    "unavailable",
    "failing",
    "partialUsage",
    "forbidden",
    "providersFail",
    "providersEmpty",
  ],
  fallback: "seeded",
});

export const setClientProfileScenario = scenarioSwitch.set;

/** How long a profile check reports "Checking" before its result lands. */
export const PROFILE_CHECK_DURATION_MS = 900;

/** A passed check older than this is reported stale. */
export const PROFILE_CHECK_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

const STORAGE_KEY = "tlx-mock:v1:oauth-client-profiles";

interface StoredValidation {
  state: ProfileCheckState;
  checks: ProfileCheck[];
  inputFingerprint: string;
  profileRevision?: number;
  checkedAt: string;
  documentFingerprint?: string;
}

interface StoredProfile {
  id: string;
  revision: number;
  name: string;
  description?: string;
  environmentIds: string[];
  purposes: ProfilePurpose[];
  lifecycle: ProfileLifecycle;
  identityLocked: boolean;
  registration: ProfileRegistration;
  managedCredentialRef?: string;
  credentialAvailability?: CredentialAvailability;
  validation?: StoredValidation;
  preview?: ProfileMetadataPreview;
  createdAt: string;
  updatedAt: string;
  activity: ProfileActivity[];
}

interface StoredJob {
  id: string;
  input: ProfileInput;
  inputFingerprint: string;
  profileId?: string;
  startedAt: string;
  cancelled?: boolean;
  /** Filled once, when the job settles. */
  validationRef?: string;
}

interface StoredRef {
  inputFingerprint: string;
  validation: StoredValidation;
  preview?: ProfileMetadataPreview;
}

interface Snapshot {
  profiles: StoredProfile[];
  /** Observed version of each published document, by URL. */
  documentVersions: Record<string, number>;
  jobs: StoredJob[];
  validationRefs: Record<string, StoredRef>;
  /** Create idempotency: key → profile id. */
  idempotency: Record<string, string>;
  sequence: number;
}

const iso = (time: number) => new Date(time).toISOString();

const PURPOSE_OF: Record<AccessPurpose, ProfilePurpose> = {
  METADATA_DISCOVERY: "METADATA_DISCOVERY",
  RUNTIME: "RUNTIME_ACCESS",
};

const fail = (
  code: ProfileErrorCode,
  message: string,
  extra: Partial<ProfileError> = {},
): never => {
  throw Object.assign(new Error(message), { code, ...extra });
};


interface UsageIndex {
  /** Distinct `service/connection` keys per profile. */
  connections: Map<string, Set<string>>;
  gateway: Map<string, number>;
}

function indexUsage(
  connections: ClientProfileConnection[],
  gatewayRefs: GatewayProfileReference[],
): UsageIndex {
  const index: UsageIndex = { connections: new Map(), gateway: new Map() };
  connections.forEach(entry => {
    const keys = index.connections.get(entry.profileId) ?? new Set<string>();
    keys.add(`${entry.serviceId}/${entry.connectionId}`);
    index.connections.set(entry.profileId, keys);
  });
  gatewayRefs.forEach(entry =>
    index.gateway.set(entry.profileId, (index.gateway.get(entry.profileId) ?? 0) + 1),
  );
  return index;
}

const inputOfSeed = (seed: ProfileSeed): ProfileInput => ({
  name: seed.name,
  description: seed.description,
  environmentIds: seed.environmentIds,
  purposes: seed.purposes,
  registration:
    seed.registration.kind === "CIMD"
      ? { kind: "CIMD", clientIdUrl: seed.registration.clientIdUrl }
      : seed.registration.kind === "EXISTING_OAUTH_CLIENT"
        ? {
            kind: "EXISTING_OAUTH_CLIENT",
            clientId: seed.registration.clientId,
            ...(seed.registration.providerId
              ? { providerId: seed.registration.providerId }
              : { issuer: seed.registration.issuer }),
          }
        : { kind: "EXISTING_OAUTH_CLIENT" },
  managedCredentialRef: seed.managedCredentialRef,
});

const inputOf = (profile: StoredProfile): ProfileInput =>
  inputOfSeed({ ...profile, createdAgoMs: 0, updatedAgoMs: 0, activity: [] });

function seedSnapshot(scenario: ClientProfileScenario, now: number): Snapshot {
  const documentVersions: Record<string, number> = {};
  const profiles =
    scenario === "empty" || scenario === "unavailable"
      ? []
      : PROFILE_SEEDS.map((seed): StoredProfile => {
          const url =
            seed.registration.kind === "CIMD"
              ? seed.registration.clientIdUrl
              : undefined;
          if (url) {
            documentVersions[url] = seed.documentChangedSinceCheck ? 2 : 1;
          }
          const input = inputOfSeed(seed);
          const checkedAt =
            seed.checkedAgoMs !== undefined
              ? iso(now - seed.checkedAgoMs)
              : undefined;
          return {
            id: seed.id,
            revision: 1,
            name: seed.name,
            description: seed.description,
            environmentIds: [...seed.environmentIds],
            purposes: [...seed.purposes],
            lifecycle: seed.lifecycle,
            identityLocked: seed.identityLocked,
            registration: structuredClone(seed.registration),
            managedCredentialRef: seed.managedCredentialRef,
            credentialAvailability: seed.credentialAvailability,
            validation:
              checkedAt && seed.checkState
                ? {
                    state: seed.checkState,
                    checks: structuredClone(seed.checks ?? []),
                    inputFingerprint: profileInputFingerprint(input),
                    profileRevision: 1,
                    checkedAt,
                    documentFingerprint: url
                      ? documentFingerprintOf(url, 1)
                      : undefined,
                  }
                : undefined,
            createdAt: iso(now - seed.createdAgoMs),
            updatedAt: iso(now - seed.updatedAgoMs),
            activity: seed.activity.map((entry, index) => ({
              id: `${seed.id}:${index}`,
              type: entry.type,
              at: iso(now - entry.agoMs),
            })),
          };
        });
  return {
    profiles,
    documentVersions,
    jobs: [],
    validationRefs: {},
    idempotency: {},
    sequence: 0,
  };
}

const isSnapshot = (value: unknown): value is Snapshot =>
  isRecord(value) &&
  Array.isArray(value.profiles) &&
  isRecord(value.documentVersions) &&
  Array.isArray(value.jobs) &&
  isRecord(value.validationRefs) &&
  isRecord(value.idempotency) &&
  typeof value.sequence === "number";

class OAuthClientProfileStore {
  private scope?: string;
  private raw?: string | null;
  private state: Snapshot = seedSnapshot("seeded", Date.now());

  /* ------------------------------------------------------------ persistence */

  private scenario(): ClientProfileScenario {
    return scenarioSwitch.get();
  }

  private scopeKey(): string {
    return JSON.stringify([currentTenant(), this.scenario()]);
  }

  /** Re-reads storage on every call, so another tab's writes are seen. */
  private load(now: number): void {
    const scope = this.scopeKey();
    let raw: string | null;
    try {
      raw = localStorage.getItem(STORAGE_KEY);
    } catch {
      raw = null;
    }
    if (scope === this.scope && raw === this.raw) return;
    this.scope = scope;
    this.raw = raw;
    try {
      const saved: unknown = raw ? JSON.parse(raw) : undefined;
      if (isRecord(saved) && saved.scope === scope && isSnapshot(saved.state)) {
        this.state = saved.state;
        return;
      }
    } catch {
      // Invalid storage never prevents the page from loading.
    }
    this.state = seedSnapshot(this.scenario(), now);
  }

  private persist(): void {
    try {
      const raw = JSON.stringify({ scope: this.scope, state: this.state });
      localStorage.setItem(STORAGE_KEY, raw);
      this.raw = raw;
    } catch {
      // Storage can be blocked; the in-memory state still works.
    }
  }

  reset(): void {
    this.scope = undefined;
    this.raw = undefined;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Nothing to clear.
    }
  }

  /* ------------------------------------------------------------- guards */

  private requireRegistry(): void {
    const scenario = this.scenario();
    if (scenario === "unavailable") {
      fail(
        PROFILE_ERRORS.UNAVAILABLE,
        "Client profile management is not available in this environment.",
      );
    }
    if (scenario === "forbidden") {
      fail(
        PROFILE_ERRORS.FORBIDDEN,
        "You do not have permission to view client profiles.",
      );
    }
  }

  private find(id: string): StoredProfile {
    const profile = this.state.profiles.find(entry => entry.id === id);
    if (!profile) fail(PROFILE_ERRORS.NOT_FOUND, "Client profile not found.");
    return profile as StoredProfile;
  }

  private nextId(prefix: string): string {
    this.state.sequence += 1;
    return `${prefix}-${Date.now().toString(36)}-${this.state.sequence}`;
  }

  /* ------------------------------------------------------------- derivations */

  private documentVersion(url: string | undefined): number {
    return url ? (this.state.documentVersions[url] ?? 1) : 1;
  }

  private credentialAvailability(profile: StoredProfile): CredentialAvailability {
    return credentialAvailabilityOf(
      MANAGED_CREDENTIALS,
      profile.managedCredentialRef,
      profile.credentialAvailability,
    );
  }

  /** The recorded check as it stands now: current, stale or never run. */
  private validationOf(
    profile: StoredProfile,
    now: number,
  ): ProfileValidation {
    const fingerprint = profileInputFingerprint(inputOf(profile));
    const stored = profile.validation;
    if (profile.registration.kind === "LEGACY_READ_ONLY") {
      return { state: "NOT_APPLICABLE", checks: [], inputFingerprint: fingerprint };
    }
    if (!stored) {
      return { state: "NOT_CHECKED", checks: [], inputFingerprint: fingerprint };
    }
    const base: ProfileValidation = {
      state: stored.state,
      checks: stored.checks,
      inputFingerprint: stored.inputFingerprint,
      profileRevision:
        stored.profileRevision !== undefined
          ? String(stored.profileRevision)
          : undefined,
      checkedAt: stored.checkedAt,
      documentFingerprint: stored.documentFingerprint,
      specificationProfile:
        profile.registration.kind === "CIMD"
          ? CIMD_SPECIFICATION_PROFILE
          : undefined,
    };
    if (stored.inputFingerprint !== fingerprint) {
      return { ...base, state: "STALE", staleReason: "INPUT_CHANGED" };
    }
    if (stored.state !== "PASSED") return base;
    const url =
      profile.registration.kind === "CIMD"
        ? profile.registration.clientIdUrl
        : undefined;
    if (
      url &&
      stored.documentFingerprint !==
        documentFingerprintOf(url, this.documentVersion(url))
    ) {
      return { ...base, state: "STALE", staleReason: "DOCUMENT_CHANGED" };
    }
    if (now - Date.parse(stored.checkedAt) > PROFILE_CHECK_MAX_AGE_MS) {
      return { ...base, state: "STALE", staleReason: "CHECK_AGED" };
    }
    return base;
  }

  private connections(now: number): ClientProfileConnection[] {
    return agentEstateStore.clientProfileConnections(now);
  }

  private usageCompleteness(): UsageCompleteness {
    return this.scenario() === "partialUsage" ? "PARTIAL" : "COMPLETE";
  }

  /** Every profile's usage, grouped once per request. */
  private usageIndex(now: number): UsageIndex {
    return indexUsage(
      this.connections(now),
      agentEstateStore.gatewayClientProfileReferences(now),
    );
  }

  private usageSummaryOf(id: string, index: UsageIndex): ProfileUsageSummary {
    return {
      distinctConnectionCount: index.connections.get(id)?.size ?? 0,
      gatewayOrDiscoveryReferenceCount: index.gateway.get(id) ?? 0,
      completeness: this.usageCompleteness(),
    };
  }

  private allowedActions(profile: StoredProfile): ProfileAction[] {
    if (profile.registration.kind === "LEGACY_READ_ONLY") return ["VIEW_USAGE"];
    if (profile.lifecycle === "ARCHIVED") return ["RESTORE", "VIEW_USAGE"];
    const input = inputOf(profile);
    return isProfileComplete(input)
      ? ["EDIT", "VIEW_USAGE", "ARCHIVE", "VALIDATE"]
      : ["EDIT", "VIEW_USAGE", "ARCHIVE"];
  }

  private toProfile(
    profile: StoredProfile,
    now: number,
    index = this.usageIndex(now),
  ): OAuthClientProfile {
    const validation = this.validationOf(profile, now);
    const availability = this.credentialAvailability(profile);
    const credential = MANAGED_CREDENTIALS.find(
      entry => entry.id === profile.managedCredentialRef,
    );
    const registration =
      profile.registration.kind === "EXISTING_OAUTH_CLIENT"
        ? {
            ...profile.registration,
            providerName: this.providers().find(
              entry =>
                profile.registration.kind === "EXISTING_OAUTH_CLIENT" &&
                entry.id === profile.registration.providerId,
            )?.name,
            issuerName: authorizationServerFor(profile.registration.issuer)
              ?.name,
          }
        : profile.registration;
    return {
      id: profile.id,
      tenantId: currentTenant() || "mock-tenant",
      revision: String(profile.revision),
      name: profile.name,
      description: profile.description,
      environmentIds: profile.environmentIds,
      purposes: profile.purposes,
      lifecycle: profile.lifecycle,
      identityLocked: profile.identityLocked,
      registration,
      clientAuthentication:
        profile.registration.kind === "LEGACY_READ_ONLY"
          ? undefined
          : {
              method: credential?.method,
              managedCredentialRef: profile.managedCredentialRef,
              referenceLabel: credential?.label,
              availability,
            },
      validation,
      metadataPreview: profile.preview,
      usage: this.usageSummaryOf(profile.id, index),
      createdAt: profile.createdAt,
      updatedAt: profile.updatedAt,
      activity: [...profile.activity].reverse(),
      allowedActions: this.allowedActions(profile),
    };
  }

  private activity(profile: StoredProfile, type: ProfileActivityType, now: number) {
    profile.activity.push({
      id: `${profile.id}:${profile.activity.length}:${now}`,
      type,
      at: iso(now),
    });
  }

  /* ---------------------------------------------------------------- reads */

  list(query: ProfileListQuery = {}, now = Date.now()): ProfilePage {
    this.load(now);
    const scenario = this.scenario();
    if (scenario === "failing") {
      throw new Error("Client profiles could not be loaded.");
    }
    if (scenario === "forbidden") this.requireRegistry();
    const management = scenario === "unavailable" ? "UNAVAILABLE" : "AVAILABLE";
    const index = this.usageIndex(now);
    const search = query.search?.trim().toLowerCase();
    const items = this.state.profiles
      .map(profile => this.toProfile(profile, now, index))
      .filter(profile => {
        if (search) {
          const host =
            profile.registration.kind === "CIMD" && profile.registration.clientIdUrl
              ? safeHost(profile.registration.clientIdUrl)
              : "";
          if (
            !profile.name.toLowerCase().includes(search) &&
            !host.toLowerCase().includes(search)
          ) {
            return false;
          }
        }
        if (
          query.registrationKind &&
          profile.registration.kind !== query.registrationKind
        ) {
          return false;
        }
        if (query.lifecycle === "NEEDS_ATTENTION") return needsAttention(profile);
        if (query.lifecycle && profile.lifecycle !== query.lifecycle) return false;
        return true;
      })
      .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt));
    return {
      items,
      total: items.length,
      capabilities: {
        management,
        validation: management,
        usage: this.usageCompleteness(),
      },
    };
  }

  get(id: string, now = Date.now()): OAuthClientProfile {
    this.load(now);
    this.requireRegistry();
    return this.toProfile(this.find(id), now);
  }

  formOptions(now = Date.now()): ProfileFormOptions {
    this.load(now);
    this.requireRegistry();
    return {
      environments: TENANT_ENVIRONMENTS,
      credentials: MANAGED_CREDENTIALS,
    };
  }

  /* ------------------------------------------------ authorization providers */

  /** Test-only override; `undefined` restores the demo integrations. */
  private providerOverride?: AuthorizationProvider[];

  /**
   * Isolated specs inject fully intercepted provider records here - e.g. one
   * with a configured issuer. Never used by the demo, which has no real
   * provider configuration.
   */
  setProvidersForTest(providers: AuthorizationProvider[] | undefined): void {
    this.providerOverride = providers ? structuredClone(providers) : undefined;
  }

  private providers(): AuthorizationProvider[] {
    if (this.providerOverride) return this.providerOverride;
    return this.scenario() === "providersEmpty"
      ? []
      : DEMO_AUTHORIZATION_PROVIDERS;
  }

  listProviders(now = Date.now()): AuthorizationProviderList {
    this.load(now);
    this.requireRegistry();
    if (this.scenario() === "providersFail") {
      throw new Error("Authorization providers could not be loaded.");
    }
    return { providers: structuredClone(this.providers()) };
  }

  getProvider(id: string, now = Date.now()): AuthorizationProvider {
    this.load(now);
    this.requireRegistry();
    const provider = this.providers().find(entry => entry.id === id);
    if (!provider) fail(PROFILE_ERRORS.NOT_FOUND, "Provider not found.");
    return structuredClone(provider as AuthorizationProvider);
  }

  usage(id: string, now = Date.now()): ProfileUsage {
    this.load(now);
    this.requireRegistry();
    const profile = this.find(id);
    const allConnections = this.connections(now);
    const allGatewayRefs = agentEstateStore.gatewayClientProfileReferences(now);
    const connections = allConnections.filter(entry => entry.profileId === id);
    const gatewayRefs = allGatewayRefs.filter(entry => entry.profileId === id);
    const references: ProfileUsageReference[] = [
      ...gatewayRefs.map(entry => ({
        id: `gateway:${entry.issuerName}`,
        type: "GATEWAY_DEFAULT" as const,
        resourceId: "mcp-access-gateway",
        resourceName: entry.gatewayName,
        connectionName: entry.issuerName,
        purposes: ["METADATA_DISCOVERY", "RUNTIME_ACCESS"] as ProfilePurpose[],
        environmentName: entry.environmentName,
      })),
      ...connections.map(entry => ({
        id: `${entry.serviceId}/${entry.connectionId}`,
        type: entry.inherited
          ? ("INHERITED_GATEWAY" as const)
          : ("MCP_CONNECTION" as const),
        resourceId: entry.serviceId,
        resourceName: entry.serviceName,
        connectionName: entry.connectionName,
        purposes: entry.purposes.map(purpose => PURPOSE_OF[purpose]),
        environmentName: entry.environmentName,
      })),
    ];
    const agents = new Set(connections.flatMap(entry => entry.agentIds));
    const completeness = this.usageCompleteness();
    return {
      summary: this.usageSummaryOf(
        profile.id,
        indexUsage(allConnections, allGatewayRefs),
      ),
      references,
      affectedAgentCount: completeness === "COMPLETE" ? agents.size : null,
    };
  }

  /* ---------------------------------------------------------------- checks */

  validate(request: ValidateProfileRequest, now = Date.now()): ProfileValidationJob {
    this.load(now);
    this.requireRegistry();
    if (request.profileId) this.find(request.profileId);
    const inputFingerprint = profileInputFingerprint(request.input);
    // One job per input at a time; a repeated request joins the running one.
    const running = this.state.jobs.find(
      job =>
        !job.cancelled &&
        !job.validationRef &&
        job.inputFingerprint === inputFingerprint &&
        job.profileId === request.profileId,
    );
    if (running) return this.jobView(running);
    const job: StoredJob = {
      id: this.nextId("check"),
      input: structuredClone(request.input),
      inputFingerprint,
      profileId: request.profileId,
      startedAt: iso(now),
    };
    this.state.jobs = [...this.state.jobs.slice(-40), job];
    this.persist();
    return this.jobView(job);
  }

  cancelValidation(jobId: string, now = Date.now()): ProfileValidationJob {
    this.load(now);
    const job = this.state.jobs.find(entry => entry.id === jobId);
    if (!job) fail(PROFILE_ERRORS.NOT_FOUND, "Check not found.");
    const found = job as StoredJob;
    if (!found.validationRef) found.cancelled = true;
    this.persist();
    return this.jobView(found);
  }

  getValidation(jobId: string, now = Date.now()): ProfileValidationJob {
    this.load(now);
    const job = this.state.jobs.find(entry => entry.id === jobId);
    if (!job) fail(PROFILE_ERRORS.NOT_FOUND, "Check not found.");
    return this.jobView(this.settle(job as StoredJob, now));
  }

  private settle(job: StoredJob, now: number): StoredJob {
    if (job.cancelled || job.validationRef) return job;
    if (now - Date.parse(job.startedAt) < PROFILE_CHECK_DURATION_MS) return job;
    const url =
      job.input.registration.kind === "CIMD"
        ? job.input.registration.clientIdUrl
        : undefined;
    const checkedAt = iso(Date.parse(job.startedAt) + PROFILE_CHECK_DURATION_MS);
    const outcome = runProfileChecks(job.input, {
      credentials: MANAGED_CREDENTIALS,
      providers: this.providers(),
      knownIssuers: AUTHORIZATION_SERVERS.map(server => server.issuer),
      documentVersion: this.documentVersion(url),
      now: Date.parse(checkedAt),
    });
    const ref = `vref-${hashString(`${job.id}|${job.inputFingerprint}`).toString(36)}`;
    const validation: StoredValidation = {
      state: outcome.state,
      checks: outcome.checks,
      inputFingerprint: job.inputFingerprint,
      checkedAt,
      documentFingerprint: outcome.preview?.documentFingerprint,
    };
    this.state.validationRefs[ref] = {
      inputFingerprint: job.inputFingerprint,
      validation,
      preview: outcome.preview,
    };
    job.validationRef = ref;
    // A saved profile's evidence updates only if its inputs are still the ones
    // checked - a result for an older input never lands on a newer one.
    if (job.profileId) {
      const profile = this.state.profiles.find(entry => entry.id === job.profileId);
      if (
        profile &&
        profileInputFingerprint(inputOf(profile)) === job.inputFingerprint
      ) {
        profile.validation = { ...validation, profileRevision: profile.revision };
        profile.preview = outcome.preview;
        this.activity(profile, "VALIDATED", Date.parse(checkedAt));
      }
    }
    this.persist();
    return job;
  }

  private jobView(job: StoredJob): ProfileValidationJob {
    const stored = job.validationRef
      ? this.state.validationRefs[job.validationRef]
      : undefined;
    return {
      id: job.id,
      state: job.cancelled ? "CANCELLED" : stored ? "DONE" : "CHECKING",
      inputFingerprint: job.inputFingerprint,
      profileId: job.profileId,
      startedAt: job.startedAt,
      result: stored
        ? {
            state: stored.validation.state,
            checks: stored.validation.checks,
            inputFingerprint: stored.inputFingerprint,
            validationRef: job.validationRef,
            checkedAt: stored.validation.checkedAt,
            documentFingerprint: stored.validation.documentFingerprint,
            specificationProfile:
              job.input.registration.kind === "CIMD"
                ? CIMD_SPECIFICATION_PROFILE
                : undefined,
          }
        : undefined,
      preview: stored?.preview,
    };
  }

  /** A passed check of exactly `input`, or undefined. Never a UI flag. */
  private passedRef(
    validationRef: string | undefined,
    input: ProfileInput,
  ): StoredRef | undefined {
    const stored = validationRef ? this.state.validationRefs[validationRef] : undefined;
    return stored &&
      stored.validation.state === "PASSED" &&
      stored.inputFingerprint === profileInputFingerprint(input)
      ? stored
      : undefined;
  }

  /* ------------------------------------------------------------- mutations */

  private assertUnique(input: ProfileInput, exceptId?: string): void {
    const key = identityKey(input.registration);
    if (!key) return;
    const existing = this.state.profiles.find(
      profile => profile.id !== exceptId && identityKey(profile.registration) === key,
    );
    if (existing) {
      fail(PROFILE_ERRORS.DUPLICATE, "A profile for this OAuth client already exists.", {
        existingProfileId: existing.id,
      });
    }
  }

  private registrationOf(input: ProfileInput): ProfileRegistration {
    return input.registration.kind === "CIMD"
      ? {
          kind: "CIMD",
          metadataSource: "EXISTING_PUBLISHED_DOCUMENT",
          clientIdUrl: input.registration.clientIdUrl,
        }
      : this.registeredApplicationOf(input.registration);
  }

  /**
   * The issuer comes from the selected integration's configuration - never
   * from the browser, and never a documented format.
   */
  private registeredApplicationOf(
    registration: Extract<
      ProfileInput["registration"],
      { kind: "EXISTING_OAUTH_CLIENT" }
    >,
  ): ProfileRegistration {
    const { providerId, clientId } = registration;
    if (!providerId) {
      return { kind: "EXISTING_OAUTH_CLIENT", clientId, issuer: registration.issuer };
    }
    const provider = this.providers().find(entry => entry.id === providerId);
    return {
      kind: "EXISTING_OAUTH_CLIENT",
      clientId,
      providerId,
      issuer: isConcreteIssuer(provider?.issuer) ? provider?.issuer : undefined,
    };
  }

  create(request: CreateProfileRequest, now = Date.now()): OAuthClientProfile {
    this.load(now);
    this.requireRegistry();
    const replay = this.state.idempotency[request.idempotencyKey];
    if (replay && this.state.profiles.some(entry => entry.id === replay)) {
      return this.get(replay, now);
    }
    const { input } = request;
    if (!isDraftSavable(input)) {
      fail(PROFILE_ERRORS.INVALID, "A profile needs a name.");
    }
    this.assertUnique(input);
    const passed = this.passedRef(request.validationRef, input);
    const available = request.lifecycle === "AVAILABLE";
    if (available && (!isProfileComplete(input) || !passed)) {
      fail(
        PROFILE_ERRORS.VALIDATION_REQUIRED,
        "Complete the profile checks before making it available.",
      );
    }
    const id = this.nextId("ocp");
    const profile: StoredProfile = {
      id,
      revision: 1,
      name: input.name,
      description: input.description,
      environmentIds: [...input.environmentIds],
      purposes: [...input.purposes],
      lifecycle: available ? "AVAILABLE" : "DRAFT",
      identityLocked: available,
      registration: this.registrationOf(input),
      managedCredentialRef: input.managedCredentialRef,
      validation: passed
        ? { ...passed.validation, profileRevision: 1 }
        : undefined,
      preview: passed?.preview,
      createdAt: iso(now),
      updatedAt: iso(now),
      activity: [],
    };
    this.activity(profile, "CREATED", now);
    if (available) this.activity(profile, "MADE_AVAILABLE", now);
    this.state.profiles = [profile, ...this.state.profiles];
    this.state.idempotency[request.idempotencyKey] = id;
    this.persist();
    return this.toProfile(profile, now);
  }

  private impactToken(profile: StoredProfile, input: ProfileInput): string {
    return `impact-${hashString(
      `${profile.id}|${profile.revision}|${JSON.stringify(input)}`,
    ).toString(36)}`;
  }

  previewImpact(
    params: { id: string; input: ProfileInput },
    now = Date.now(),
  ): ProfileImpact {
    this.load(now);
    this.requireRegistry();
    const profile = this.find(params.id);
    const current = this.toProfile(profile, now);
    const consequentialFields = consequentialChanges(current, params.input);
    const identityChange = identityChanges(current, params.input);
    const hasReferences =
      (current.usage.distinctConnectionCount ?? 0) +
        (current.usage.gatewayOrDiscoveryReferenceCount ?? 0) >
      0;
    const blocked =
      consequentialFields.length > 0 && current.usage.completeness !== "COMPLETE";
    return {
      consequentialFields,
      identityChange,
      identityChangeBlocked: identityChange && profile.identityLocked,
      usage: current.usage,
      blocked,
      impactToken:
        !blocked && (consequentialFields.length > 0 || hasReferences)
          ? this.impactToken(profile, params.input)
          : undefined,
    };
  }

  update(request: UpdateProfileRequest, now = Date.now()): OAuthClientProfile {
    this.load(now);
    this.requireRegistry();
    const profile = this.find(request.id);
    if (request.revision !== String(profile.revision)) {
      fail(PROFILE_ERRORS.VERSION_CONFLICT, "This profile was updated by someone else.");
    }
    if (profile.registration.kind === "LEGACY_READ_ONLY") {
      fail(PROFILE_ERRORS.INVALID, "This profile is managed where it was created.");
    }
    if (profile.lifecycle === "ARCHIVED") {
      fail(PROFILE_ERRORS.INVALID, "Restore this profile before editing it.");
    }
    const { input } = request;
    if (!isDraftSavable(input)) fail(PROFILE_ERRORS.INVALID, "A profile needs a name.");
    const current = this.toProfile(profile, now);
    if (identityChanges(current, input)) {
      if (profile.identityLocked) {
        fail(
          PROFILE_ERRORS.IDENTITY_LOCKED,
          "The OAuth client identity of an available profile cannot be replaced.",
        );
      }
      this.assertUnique(input, profile.id);
    }
    const consequential = consequentialChanges(current, input);
    if (consequential.length > 0) {
      if (current.usage.completeness !== "COMPLETE") {
        fail(
          PROFILE_ERRORS.USAGE_UNKNOWN,
          "Usage is not fully known, so this change cannot be assessed.",
        );
      }
      const referenced =
        (current.usage.distinctConnectionCount ?? 0) +
          (current.usage.gatewayOrDiscoveryReferenceCount ?? 0) >
        0;
      if (referenced && request.impactToken !== this.impactToken(profile, input)) {
        fail(
          PROFILE_ERRORS.IMPACT_REVIEW_REQUIRED,
          "Review the impact of this change before saving it.",
        );
      }
    }
    const nextFingerprint = profileInputFingerprint(input);
    const inputsChanged =
      nextFingerprint !== profileInputFingerprint(inputOf(profile));
    const passed = this.passedRef(request.validationRef, input);
    // An available profile never becomes unchecked: new inputs need a pass.
    if (profile.lifecycle === "AVAILABLE" && inputsChanged && !passed) {
      fail(
        PROFILE_ERRORS.VALIDATION_REQUIRED,
        "Check this configuration before saving it on an available profile.",
      );
    }
    profile.revision += 1;
    profile.name = input.name;
    profile.description = input.description;
    profile.environmentIds = [...input.environmentIds];
    profile.purposes = [...input.purposes];
    profile.registration = this.registrationOf(input);
    profile.managedCredentialRef = input.managedCredentialRef;
    if (passed) {
      profile.validation = { ...passed.validation, profileRevision: profile.revision };
      profile.preview = passed.preview;
    } else if (inputsChanged) {
      profile.validation = undefined;
      profile.preview = undefined;
    }
    profile.updatedAt = iso(now);
    this.activity(profile, "UPDATED", now);
    if (request.makeAvailable && profile.lifecycle === "DRAFT") {
      const validation = this.validationOf(profile, now);
      const credential = this.credentialAvailability(profile);
      if (
        !isProfileComplete(input) ||
        validation.state !== "PASSED" ||
        credential === "MISSING" ||
        credential === "RESTRICTED"
      ) {
        profile.revision -= 1;
        fail(
          PROFILE_ERRORS.VALIDATION_REQUIRED,
          "Complete the profile checks before making it available.",
        );
      }
      profile.lifecycle = "AVAILABLE";
      profile.identityLocked = true;
      this.activity(profile, "MADE_AVAILABLE", now);
    }
    this.persist();
    return this.toProfile(profile, now);
  }

  archive(params: { id: string; revision: string }, now = Date.now()) {
    return this.transition(params, now, "ARCHIVED");
  }

  /** Back to Draft, with its checks cleared: they must run again. */
  restore(params: { id: string; revision: string }, now = Date.now()) {
    return this.transition(params, now, "DRAFT");
  }

  private transition(
    params: { id: string; revision: string },
    now: number,
    lifecycle: "ARCHIVED" | "DRAFT",
  ): OAuthClientProfile {
    this.load(now);
    this.requireRegistry();
    const profile = this.find(params.id);
    if (params.revision !== String(profile.revision)) {
      fail(PROFILE_ERRORS.VERSION_CONFLICT, "This profile was updated by someone else.");
    }
    const allowed =
      lifecycle === "ARCHIVED"
        ? profile.lifecycle !== "ARCHIVED" &&
          profile.registration.kind !== "LEGACY_READ_ONLY"
        : profile.lifecycle === "ARCHIVED";
    if (!allowed) fail(PROFILE_ERRORS.INVALID, "This action is not available.");
    profile.lifecycle = lifecycle;
    if (lifecycle === "DRAFT") {
      profile.validation = undefined;
      profile.preview = undefined;
    }
    profile.revision += 1;
    profile.updatedAt = iso(now);
    this.activity(profile, lifecycle === "ARCHIVED" ? "ARCHIVED" : "RESTORED", now);
    this.persist();
    return this.toProfile(profile, now);
  }

  /* ---------------------------------------------------------------- picker */

  private eligibilityOf(
    profile: OAuthClientProfile,
    context: ProfileSelectionContext,
  ): { eligibility: ProfileEligibility; reasons: EligibilityReason[] } {
    const reasons: EligibilityReason[] = [];
    if (profile.registration.kind !== context.registrationKind) {
      return { eligibility: "INELIGIBLE", reasons: ["METHOD_MISMATCH"] };
    }
    if (profile.lifecycle === "ARCHIVED") {
      return { eligibility: "EXISTING_REFERENCE_ONLY", reasons: ["ARCHIVED"] };
    }
    if (profile.lifecycle === "DRAFT") reasons.push("DRAFT");
    if (!profile.purposes.includes(context.purpose)) {
      reasons.push("PURPOSE_RESTRICTED");
    }
    if (
      context.environmentId &&
      profile.environmentIds.length > 0 &&
      !profile.environmentIds.includes(context.environmentId)
    ) {
      reasons.push("ENVIRONMENT_RESTRICTED");
    }
    const availability = profile.clientAuthentication?.availability;
    if (availability === "MISSING" || availability === "RESTRICTED") {
      reasons.push("CREDENTIAL_UNAVAILABLE");
    }
    if (profile.validation.state === "FAILED") reasons.push("VALIDATION_FAILED");
    if (
      profile.registration.kind === "EXISTING_OAUTH_CLIENT" &&
      context.targetIssuer &&
      profile.registration.issuer !== context.targetIssuer
    ) {
      // A pre-registered client is bound to the issuer it was registered with.
      reasons.push("ISSUER_MISMATCH");
    }
    if (profile.registration.kind === "CIMD" && context.targetIssuer) {
      const server = authorizationServerFor(context.targetIssuer);
      if (server?.cimd === "UNSUPPORTED") reasons.push("ISSUER_UNSUPPORTED");
    }
    if (reasons.length > 0) return { eligibility: "INELIGIBLE", reasons };

    // Selectable - but what is not yet known is said, never assumed.
    const pending: EligibilityReason[] = [];
    if (profile.validation.state === "STALE") pending.push("VALIDATION_STALE");
    if (!context.targetIssuer) pending.push("ISSUER_UNKNOWN");
    else if (
      profile.registration.kind === "CIMD" &&
      authorizationServerFor(context.targetIssuer)?.cimd !== "SUPPORTED"
    ) {
      pending.push("CONTEXT_UNKNOWN");
    }
    return pending.length
      ? { eligibility: "CONTEXT_CHECK_REQUIRED", reasons: pending }
      : { eligibility: "ELIGIBLE", reasons: [] };
  }

  private optionOf(
    profile: OAuthClientProfile,
    context: ProfileSelectionContext,
  ): ProfileOption {
    const { eligibility, reasons } = this.eligibilityOf(profile, context);
    return {
      id: profile.id,
      revision: profile.revision,
      name: profile.name,
      registrationKind: profile.registration.kind,
      clientIdUrl:
        profile.registration.kind === "CIMD"
          ? profile.registration.clientIdUrl
          : undefined,
      environmentLabels: profile.environmentIds.map(
        id => TENANT_ENVIRONMENTS.find(env => env.id === id)?.name ?? id,
      ),
      purposes: profile.purposes,
      lifecycle: profile.lifecycle,
      validationState: profile.validation.state,
      checkedAt: profile.validation.checkedAt,
      eligibility,
      reasonCodes: reasons,
    };
  }

  options(context: ProfileSelectionContext, now = Date.now()): ProfileOptionsResult {
    this.load(now);
    const scenario = this.scenario();
    if (scenario === "failing") {
      throw new Error("Client profiles could not be loaded.");
    }
    if (scenario === "forbidden") {
      return { state: "FORBIDDEN", options: [], draftsOnly: false };
    }
    if (scenario === "unavailable") {
      return { state: "UNAVAILABLE", options: [], draftsOnly: false };
    }
    const index = this.usageIndex(now);
    const profiles = this.state.profiles
      .filter(profile => profile.registration.kind === context.registrationKind)
      .map(profile => this.toProfile(profile, now, index));
    const options = profiles
      .filter(profile => profile.lifecycle !== "ARCHIVED")
      .map(profile => this.optionOf(profile, context))
      .sort((a, b) => a.name.localeCompare(b.name));
    const currentProfile = context.currentProfileId
      ? profiles.find(profile => profile.id === context.currentProfileId)
      : undefined;
    return {
      state: "OK",
      options,
      current: currentProfile ? this.optionOf(currentProfile, context) : undefined,
      currentInaccessible: !!context.currentProfileId && !currentProfile,
      draftsOnly:
        options.length > 0 && options.every(option => option.lifecycle === "DRAFT"),
    };
  }

  /**
   * Server-side guard for a connection save: a newly chosen reference must be
   * selectable for its context. An unchanged existing reference is kept.
   */
  assertAssignable(
    reference: { profileId: string; purposes: AccessPurpose[] },
    targetIssuer: string | undefined,
    now = Date.now(),
  ): void {
    this.load(now);
    const stored = this.state.profiles.find(entry => entry.id === reference.profileId);
    if (!stored) {
      fail(PROFILE_ERRORS.NOT_ASSIGNABLE, "The selected client profile is not available.");
    }
    // OAuth edits its client in the runtime section, pass-through in the
    // discovery one: the section's purpose is the one enforced.
    const purpose: ProfilePurpose = reference.purposes.includes("RUNTIME")
      ? "RUNTIME_ACCESS"
      : "METADATA_DISCOVERY";
    const { eligibility } = this.eligibilityOf(
      this.toProfile(stored as StoredProfile, now),
      { registrationKind: "CIMD", purpose, targetIssuer },
    );
    if (eligibility === "INELIGIBLE" || eligibility === "EXISTING_REFERENCE_ONLY") {
      fail(
        PROFILE_ERRORS.NOT_ASSIGNABLE,
        "The selected client profile cannot be used for this connection.",
      );
    }
  }

  /* ---------------------------------------------------------- spec helpers */

  /** Another administrator saves this profile first. */
  simulateConcurrentEdit(id: string, now = Date.now()): void {
    this.load(now);
    const profile = this.find(id);
    profile.revision += 1;
    profile.updatedAt = iso(now);
    this.activity(profile, "UPDATED", now);
    this.persist();
  }

  /** The publisher changes the document outside TrustLogix. */
  simulateDocumentChange(url: string, now = Date.now()): void {
    this.load(now);
    this.state.documentVersions[url] = this.documentVersion(url) + 1;
    this.state.profiles
      .filter(
        profile =>
          profile.registration.kind === "CIMD" &&
          profile.registration.clientIdUrl === url,
      )
      .forEach(profile => this.activity(profile, "DOCUMENT_CHANGED", now));
    this.persist();
  }
}

const safeHost = (url: string): string => {
  try {
    return new URL(url).hostname;
  } catch {
    return "";
  }
};

export const oauthClientProfileStore = new OAuthClientProfileStore();

registerAgentSessionReset("oauth-client-profiles", () =>
  oauthClientProfileStore.reset(),
);
